import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/**
 * Admin → Messages (backend lib/cs-inbox.ts): contact-form messages from the storefront and emails to
 * the support mailbox (Gmail, backend lib/gmail/*), the customer they match, and the CS team's
 * status / category / assignee / notes.
 */

export type MessageStatus = "new" | "open" | "waiting_on_customer" | "closed"
export type MessageCategory = "cancel" | "pause_skip" | "billing_card" | "discount" | "shipping_order" | "other"
export type InboxMessage = {
  id: string
  source: string
  name: string
  email: string
  phone: string | null
  message: string
  status: MessageStatus
  category: MessageCategory
  category_set_by: string | null
  assigned_to: string | null
  customer_id: string | null
  customer_linked_by: "email" | "staff" | null
  notes: string | null
  excerpt_only: boolean
  /** Emails: the thread's subject. */
  subject?: string | null
  /** Automated sender (no-reply, bounce, newsletter…), kept out of the lists. */
  not_customer?: boolean
  /** When the customer last wrote (emails). */
  activity_at?: string | null
  replied_at: string | null
  replied_by: string | null
  closed_at: string | null
  created_at: string
  updated_at: string
  customer: { id: string; email: string; first_name: string | null; last_name: string | null; has_account: boolean } | null
  /** Replies sent from the message page, oldest first (only on the single-message answer). */
  replies?: MessageReply[]
  /** Its emails (support mailbox), oldest first (only on the single-message answer). */
  emails?: ConversationEmail[]
}
export type EmailAddress = { name: string | null; email: string }
export type ConversationEmail = {
  id: string
  direction: "in" | "out"
  /** form_notification: the contact-form email to the mailbox that links the form message to its Gmail thread (not shown). */
  kind: "email" | "form_notification"
  gmail_thread_id: string | null
  from_email: string | null
  from_name: string | null
  to_emails: EmailAddress[]
  cc_emails: EmailAddress[]
  subject: string | null
  sent_at: string
  text: string | null
  html: string | null
  attachments: { part_id: string; filename: string; mime_type: string; size: number; inline: boolean }[]
  unread: boolean
  automated: boolean
  /** Sent from here (the reply row); null = sent from Gmail or received. */
  reply_id: string | null
}
/** A reply sent from admin → Messages (backend lib/cs-inbox.ts sendReply). */
export type MessageReply = {
  id: string
  message_id: string
  sent_by: string
  sent_by_name: string | null
  sent_by_email: string | null
  to_email: string
  reply_to: string | null
  subject: string
  body: string
  /** sent: handed to the email service; logged: email isn't set up on this server; failed: not sent. */
  status: "sent" | "logged" | "failed"
  transport: string | null
  error: string | null
  created_at: string
}
/**
 * via: "store_email" = the store's email service (as before Gmail); "gmail" = through the support
 * mailbox, in the thread; "gmail_only" = an email conversation while Gmail is import-only (answer in Gmail).
 */
export type ReplyDefaults = {
  subject: string
  reply_to: string
  via?: "store_email" | "gmail" | "gmail_only"
  from?: { name: string | null; email: string | null } | null
  gmail_mode?: "off" | "import" | "full"
  /** Gmail's Google sign-in must be reconnected: replies through Gmail are refused until then (plain reason). */
  gmail_needs_reconnect?: string | null
}
export type InboxMessageResponse = { message: InboxMessage; reply_defaults?: ReplyDefaults }
export type InboxCounts = Record<MessageStatus | "active" | "all" | "unanswered", number>
export type InboxList = { messages: InboxMessage[]; count: number; counts: InboxCounts }
export type Assignee = { user_id: string; email: string; name: string | null }
export type MessageChange = {
  status?: MessageStatus
  category?: MessageCategory
  assigned_to?: string | null
  notes?: string | null
  customer_id?: string | null
  replied?: true
  not_customer?: boolean
}

export const STATUS_LABEL: Record<MessageStatus, string> = { new: "New", open: "Open", waiting_on_customer: "Waiting on customer", closed: "Closed" }
export const STATUS_TONE: Record<MessageStatus, "info" | "attention" | "neutral" | "success"> = { new: "info", open: "attention", waiting_on_customer: "neutral", closed: "success" }
export const CATEGORY_LABEL: Record<MessageCategory, string> = {
  cancel: "Cancel",
  pause_skip: "Pause or skip",
  billing_card: "Billing or card",
  discount: "Discount",
  shipping_order: "Shipping or order",
  other: "Other",
}

export function useInbox(qs: string) {
  return useQuery({ queryKey: ["inbox", "list", qs], queryFn: () => apiFetch<InboxList>(`/admin/terify/inbox?${qs}`), placeholderData: keepPreviousData, refetchInterval: 45_000 })
}

/** New messages: the number on the Messages nav item. */
export function useInboxCounts(enabled: boolean) {
  return useQuery({
    queryKey: ["inbox", "counts"],
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: () => apiFetch<{ new: number; mine: number }>("/admin/terify/inbox/counts"),
  })
}

export function useInboxMessage(id: string) {
  return useQuery({ queryKey: ["inbox", "message", id], queryFn: () => apiFetch<InboxMessageResponse>(`/admin/terify/inbox/m/${encodeURIComponent(id)}`) })
}

export function useAssignees(enabled = true) {
  return useQuery({ queryKey: ["inbox", "assignees"], enabled, staleTime: 5 * 60_000, queryFn: () => apiFetch<{ assignees: Assignee[] }>("/admin/terify/inbox/assignees") })
}

export function useUpdateMessage(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: MessageChange) => apiFetch<{ message: InboxMessage }>(`/admin/terify/inbox/m/${encodeURIComponent(id)}`, { method: "POST", body: JSON.stringify(b) }),
    // Keeps reply_defaults (only the GET has them).
    onSuccess: (r) => {
      qc.setQueryData<InboxMessageResponse>(["inbox", "message", id], (old) => ({ ...old, ...r }))
      qc.invalidateQueries({ queryKey: ["inbox", "list"] })
      qc.invalidateQueries({ queryKey: ["inbox", "counts"] })
    },
  })
}

/** "Send" in the reply box: emails the customer from the store and marks the message replied. */
export function useSendReply(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: { subject: string; body: string }) =>
      apiFetch<{ message: InboxMessage; reply: MessageReply }>(`/admin/terify/inbox/m/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify(b) }),
    onSuccess: (r) => {
      qc.setQueryData<InboxMessageResponse>(["inbox", "message", id], (old) => ({ ...old, message: r.message }))
      qc.invalidateQueries({ queryKey: ["inbox", "list"] })
      qc.invalidateQueries({ queryKey: ["inbox", "counts"] })
    },
    // A refused send is kept under the message as "Not sent": show it.
    onError: () => qc.invalidateQueries({ queryKey: ["inbox", "message", id] }),
  })
}

/** The first name the reply starts with: the first word of the name they gave on the form, else the linked customer's. */
export function replyFirstName(m: Pick<InboxMessage, "name" | "customer">) {
  return (m.name.trim().split(/\s+/)[0] || m.customer?.first_name?.trim() || "").trim()
}

/** What the reply box starts with: "Hi <first name>,", a blank line, then the staff member writes. */
export const replyGreeting = (m: Pick<InboxMessage, "name" | "customer">) => `Hi ${replyFirstName(m) || "there"},\n\n`

export const DEFAULT_REPLY_SUBJECT = "Re: your message to Tolvane Labs"

export const SOURCE_LABEL: Record<string, string> = { contact_form: "Contact form", email: "Email" }

/** The thread in Gmail's web app, signed in as the support mailbox. */
export const gmailThreadUrl = (mailbox: string | null | undefined, threadId: string) =>
  `https://mail.google.com/mail/${mailbox ? `?authuser=${encodeURIComponent(mailbox)}` : "u/0/"}#all/${encodeURIComponent(threadId)}`

/** The customer's new words, without what their email app quoted below (the full text is one click away). */
export function splitQuoted(text: string): { fresh: string; quoted: string } {
  const lines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n")
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim()
    const next = (lines[i + 1] ?? "").trim()
    const quoteStart =
      /^On\s.{0,200}\bwrote:$/i.test(l) ||
      (/^On\s.{0,200}$/i.test(l) && /\bwrote:$/i.test(next) && next.length < 120) ||
      /^-{2,}\s*Original Message\s*-{2,}$/i.test(l) ||
      (/^From:\s.+/i.test(l) && lines.slice(i + 1, i + 5).some((x) => /^(Sent|Date):\s/i.test(x.trim()))) ||
      (/^>/.test(l) && lines.slice(i).every((x) => !x.trim() || /^>/.test(x.trim())))
    if (quoteStart && i > 0) {
      const fresh = lines.slice(0, i).join("\n").replace(/\s+$/, "")
      if (fresh.trim()) return { fresh, quoted: lines.slice(i).join("\n") }
    }
  }
  return { fresh: String(text ?? "").replace(/\s+$/, ""), quoted: "" }
}

/** Saves one attachment (read from Gmail by the server). */
export async function downloadAttachment(messageId: string, emailId: string, partId: string, filename: string) {
  const base = import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin
  const res = await fetch(`${base}/admin/terify/inbox/m/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(emailId)}/${encodeURIComponent(partId)}`, { credentials: "include" })
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.message ?? "The attachment couldn't be downloaded.")
  const url = URL.createObjectURL(await res.blob())
  const a = Object.assign(document.createElement("a"), { href: url, download: filename || "attachment" })
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * "Open in Gmail" (fallback when sending from here isn't possible): Gmail's compose window in a new
 * tab, to the customer, with the subject and the text from the reply box and their message quoted.
 * The reply then goes from the staff member's own Gmail, so it isn't kept here: "Mark replied" after.
 */
export function gmailComposeUrl(m: Pick<InboxMessage, "email" | "name" | "message" | "created_at">, subject: string, draft: string) {
  const when = new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
  const quoted = m.message
    .split(/\r?\n/)
    .map((l) => `> ${l}`)
    .join("\n")
  const body = `${draft.replace(/\s+$/, "")}\n\n\nOn ${when}, ${m.name} wrote:\n${quoted}`
  // Long URLs get cut by browsers and proxies: keep the compose link well under 8 KB.
  const p = new URLSearchParams({ view: "cm", fs: "1", to: m.email, su: subject, body: body.slice(0, 3000) })
  return `https://mail.google.com/mail/?${p.toString().replace(/\+/g, "%20")}`
}
